import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import Toggle from 'react-toggle';
import { FaUndo } from 'react-icons/fa';
import isEqual from 'lodash-es/isEqual';
import intlShape from '@/shared/utils/intlShape';

import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import TextSearch from '@/shared/components/input/TextSearch';
import InteractionTagMultiSelect from './InteractionTagMultiSelect';
import messages from './interactionMessages';
import InteractionTypeButtonGroup from './InteractionTypeButtonGroup';

export const defaultFilters = {
  startDate: null,
  endDate: null,
  period: 'all',
  interactionId: null,
  showLinkedRelationInteractions: true,
  interactionTypes: [],
  search: null,
  tags: [],
};

class InteractionFilters extends Component {
  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      startDate: momentPropTypes.momentObj,
      endDate: momentPropTypes.momentObj,
      period: PropTypes.oneOf(['all', 'month', '6months']),
      search: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
    }),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: {
      startDate: null,
      endDate: null,
      period: null,
      interactionId: null,
      search: null,
      tags: null,
    },
  };

  componentDidMount() {
    const { filters } = this.props;

    if (filters && filters.period) {
      this.setPeriod(filters.period, false);
    }
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    onFilterChange(newFilters);
  };

  createDateFromChanged = value => {
    this.filterChanged({ startDate: value });
  };
  createDateToChanged = value => {
    this.filterChanged({ endDate: value });
  };

  createDateChanged = (startDate, endDate) => {
    this.filterChanged({ startDate, endDate, period: null });
  };

  setPeriod = (period, reload) => {
    let startDate = null;
    let endDate = null;
    switch (period) {
      case 'all':
      case '':
      case null:
      case undefined:
        break;
      case 'month':
        startDate = moment.utc().startOf(period);
        endDate = moment.utc().endOf(period);
        break;
      case '6months':
        startDate = moment
          .utc()
          .subtract(6, 'months')
          .startOf('day');
        endDate = moment.utc().endOf('day');
        break;
      default:
        console.debug(`Unknown period: ${period}`);
        return;
    }
    if (reload) {
      this.filterChanged({ startDate, endDate, period });
    }
  };

  toggleShowLinkedRelationInteractions = active => {
    this.filterChanged({ showLinkedRelationInteractions: !!active });
  };

  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  render() {
    const { filters, hasLinkedRelations, intl } = this.props;
    const defaultBtnClass = 'btn btn-default';

    // relationId cannot be stored in default filters constant, and we dont need it to check if filters are selected.
    const filtersToCompare = { ...filters };
    delete filtersToCompare.relationId;

    const hasFilters =
      filtersToCompare &&
      Object.keys(filtersToCompare).length !== 0 &&
      !isEqual(filtersToCompare, defaultFilters);

    return (
      <div>
        <div style={{ display: 'flex' }}>
          <div className="margin5B" style={{ flex: 1 }}>
            <InteractionTypeButtonGroup
              value={filters?.interactionTypes}
              onChange={value => this.filterChanged({ interactionTypes: value })}
              intl={intl}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <div className="margin5B" style={{ flex: 1 }}>
            <DelayedDateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onDatesChange={({ startDate, endDate }) => this.createDateChanged(startDate, endDate)}
              block
            />
          </div>
          <div className="margin5B" style={{ flex: 1 }}>
            <div role="group" aria-label="period" className="btn-group" style={{ display: 'flex' }}>
              <button
                onClick={() => this.setPeriod('month', true)}
                type="button"
                className={classnames(defaultBtnClass, { active: filters.period === 'month' })}
                style={{ flex: 1 }}
              >
                <FormattedMessage {...messages.ThisMonth} />
              </button>
              <button
                onClick={() => this.setPeriod('6months', true)}
                type="button"
                className={classnames(defaultBtnClass, {
                  active: filters.period === '6months',
                })}
                style={{ flex: 1 }}
              >
                <FormattedMessage {...messages.LastSixMonths} />
              </button>
              <button
                onClick={() => this.setPeriod('all', true)}
                type="button"
                className={classnames(defaultBtnClass, {
                  active: !filters.startDate && !filters.endDate,
                })}
                style={{ flex: 1 }}
              >
                <FormattedMessage {...messages.All} />
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <div className="margin5B" style={{ flex: 1 }}>
            <TextSearch
              value={filters.search}
              className="form-control"
              placeholder={intl.formatMessage(messages.Search)}
              onChange={value => this.filterChanged({ search: value })}
            />
          </div>
          <div className="margin5B" style={{ flex: 1 }}>
            <InteractionTagMultiSelect
              value={filters.tags}
              onChange={tags => this.filterChanged({ tags })}
              placeholder={intl.formatMessage(messages.Tags)}
            />
          </div>

          {hasLinkedRelations && (
            <div className="margin5B" style={{ flex: 1 }}>
              <label className="react-toggle-wrapper margin5T" style={{ marginRight: '30px' }}>
                <Toggle
                  checked={filters.showLinkedRelationInteractions}
                  onChange={e => this.toggleShowLinkedRelationInteractions(e.target.checked)}
                />
                <FormattedMessage
                  className="label-text"
                  {...messages.ShowInteractionsOfLinkedRelations}
                />
              </label>
            </div>
          )}
        </div>
        {hasFilters && (
          <button
            type="button"
            className="btn btn-default btn-reset"
            onClick={() => this.resetFilters()}
          >
            <FaUndo className="react-icon" />
            &nbsp;
            <FormattedMessage {...messages.ResetFilters} />
          </button>
        )}
      </div>
    );
  }
}

export default injectIntl(InteractionFilters);
